/**
 * @file useSkuExpiryPolicy.ts
 * @description Политика срока годности выбранной фасовки для строки приёмки
 * (phase19.22, W20, W22): требует ли вид товара срок и сколько дней
 * подставлять от даты изготовления.
 *
 * Складской пикер (`GET /admin/wms/skus`) вида товара не отдаёт, поэтому
 * берём карточку товара (`GET /admin/catalog/products/:id`: `shelfLifeDays` +
 * `effectiveKind`) — сервер проверяет только магазин, не право каталога.
 * Не вышло (сеть, старый сервер) — политика «неизвестна»: поля видны, расчёт
 * сделает сервер при сохранении строки. Кэш по товару — на сессию вкладки.
 */

import { useEffect, useState } from "react";

import { apiFetch } from "../../lib/api-client";
import {
  UNKNOWN_EXPIRY_POLICY,
  resolveExpiryPolicy,
  type EffectiveKindLike,
  type ExpiryPolicy,
} from "../../lib/receipt-expiry";

/** Поля карточки товара, нужные строке приёмки (зеркало `ProductDto`). */
interface ProductExpiryFields {
  shelfLifeDays: number | null;
  effectiveKind?: EffectiveKindLike | null;
}

const policyCache = new Map<string, ExpiryPolicy>();

export function useSkuExpiryPolicy(productId: string | null | undefined) {
  const [policy, setPolicy] = useState<ExpiryPolicy>(UNKNOWN_EXPIRY_POLICY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!productId) {
      setPolicy(UNKNOWN_EXPIRY_POLICY);
      return;
    }
    const cached = policyCache.get(productId);
    if (cached) {
      setPolicy(cached);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiFetch<ProductExpiryFields>(`/admin/catalog/products/${productId}`)
      .then((product) => {
        // Старый сервер без `effectiveKind` — вид неизвестен, срок «мягко» нужен.
        const next = resolveExpiryPolicy(product.shelfLifeDays, product.effectiveKind ?? null);
        policyCache.set(productId, next);
        if (!cancelled) setPolicy(next);
      })
      .catch(() => {
        if (!cancelled) setPolicy(UNKNOWN_EXPIRY_POLICY);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  return { policy, loading };
}
